// Connectivity monitor: the single place that decides "are we online".
//
// Signals, in order of trust:
//   - `offline` window event            → offline immediately (negative signal is reliable)
//   - fetch TypeError reported by apiFetch → offline + start probing
//   - `online` window event / visibility → probe, never trust blindly
//   - probe answers with a real HTTP response → online
//   - probe answers 'unauthenticated' while online → session expired → onUnauthenticated()
//     (the ONLY place after boot that may redirect to the login app)

export function createConnectivityMonitor({
  initialOnline = true,
  probe,
  onUnauthenticated,
  probeIntervalMs = 15000,
  win = typeof window !== 'undefined' ? window : null,
  doc = typeof document !== 'undefined' ? document : null,
} = {}) {
  let online = !!initialOnline;
  let timer = null;
  let probing = false;
  const listeners = new Set();

  const emit = () => listeners.forEach((fn) => { try { fn(online); } catch { /* listener error must not break the monitor */ } });

  const startTimer = () => { if (!timer && probe) timer = setInterval(runProbe, probeIntervalMs); };
  const stopTimer = () => { if (timer) { clearInterval(timer); timer = null; } };

  const setOnline = (next) => {
    const changed = next !== online;
    online = next;
    if (online) stopTimer(); else startTimer();
    if (changed) emit();
  };

  async function runProbe() {
    if (!probe || probing) return online;
    probing = true;
    try {
      const result = await probe();
      if (result === 'ok') setOnline(true);
      else if (result === 'unauthenticated') { setOnline(true); onUnauthenticated?.(); }
      else setOnline(false);
    } catch {
      setOnline(false);
    } finally {
      probing = false;
    }
    return online;
  }

  const onOffline = () => setOnline(false);
  const onOnline = () => { runProbe(); };
  const onVisible = () => { if (!online && doc?.visibilityState === 'visible') runProbe(); };

  win?.addEventListener('offline', onOffline);
  win?.addEventListener('online', onOnline);
  doc?.addEventListener('visibilitychange', onVisible);
  if (!online) startTimer();

  return {
    isOnline: () => online,
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    probe: runProbe,
    reportFailure() { setOnline(false); },
    reportSuccess() { setOnline(true); },
    dispose() {
      stopTimer();
      listeners.clear();
      win?.removeEventListener('offline', onOffline);
      win?.removeEventListener('online', onOnline);
      doc?.removeEventListener('visibilitychange', onVisible);
    },
  };
}
